import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import { Divider, Form, FormInstance } from 'antd';
import { ReactComponent as CancelIcon } from 'shared/assets/icons/CloseIcon.svg';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { ModernInput as Input } from 'shared/ui/ModernInput';
import { ModernSelect as Select } from 'shared/ui/ModernSelect';
import { useGetCurrencySymbol } from 'app/appState';
import { InputNumber } from 'shared/ui/InputNumber';
import { RadioButtonGroup } from 'shared/ui/RadioButtonGroup';
import { DiscountMode, DiscountSetting } from '../model/types';
import { parseDiscounts } from '../utils/helpers/parseDiscounts';
import { formatDiscounts } from '../utils/helpers/formatDiscount';
import { ReactComponent as PlusIcon } from 'shared/assets/icons/PlusIcon.svg';

interface DiscountSettingsFormProps {
  discountSettings?: Nullable<string[]>;
  handleClose: () => void;
  formInstance: FormInstance;
}

export const DiscountSettingsForm: FC<DiscountSettingsFormProps> = memo((props) => {
  const { discountSettings, handleClose, formInstance } = props;

  const { t } = useAppTranslation('contracts');
  const [form] = Form.useForm();
  const currencySymbol = useGetCurrencySymbol();
  const [discounts, setDiscounts] = useState<string[]>([]);

  const valueTypeOptions = [
    { label: '%', value: '%' },
    { label: currencySymbol, value: '-' },
  ];

  useEffect(() => {
    if (discountSettings?.length) {
      const parsed = parseDiscounts(discountSettings);
      form.setFieldsValue({ discountSettings: parsed });
      setDiscounts(formatDiscounts(parsed));
    }

    return () => {
      form.resetFields();
      setDiscounts([]);
    };
  }, [discountSettings, form]);

  const getDiscountModeOptions = (index: number): Array<{ label: string; value: DiscountMode }> => {
    const baseOptions = [
      { label: t('Min rental duration'), value: DiscountMode.MIN_RENTAL_DURATION },
      { label: t('Custom'), value: DiscountMode.CUSTOM },
    ];

    const discountSettings = form.getFieldValue('discountSettings');

    const hasMinRentalDuration = discountSettings.some((item: DiscountSetting) => item?.mode === DiscountMode.MIN_RENTAL_DURATION);

    if (index > 0 && hasMinRentalDuration) {
      baseOptions.push({ label: t('All periods after min rental duration'), value: DiscountMode.ENTIRE_PERIOD });
      baseOptions.shift();
    }

    return baseOptions;
  };

  const changeDiscountMode = useCallback(
    (mode: DiscountMode, index: number) => {
      const periodValue =
        mode === DiscountMode.MIN_RENTAL_DURATION
          ? DiscountMode.MIN_RENTAL_DURATION
          : mode === DiscountMode.ENTIRE_PERIOD
          ? DiscountMode.ENTIRE_PERIOD
          : '';

      const currentValues = form.getFieldValue('discountSettings') || [];
      const updatedValues = [...currentValues];
      updatedValues[index] = {
        ...updatedValues[index],
        mode,
        period: periodValue,
      };

      form.setFieldsValue({ discountSettings: updatedValues });
      setDiscounts(formatDiscounts(updatedValues));
    },
    [form],
  );

  const handleFormChange = (_: CustomAny, values: { discountSettings: DiscountSetting[] }): void => {
    const list = values.discountSettings || [];
    setDiscounts(formatDiscounts(list));
  };

  const removeDiscountSetting = useCallback(
    (index: number, remove: (index: number) => void): void => {
      const currentList = form.getFieldValue('discountSettings') || [];
      const updatedList = currentList.filter((_: CustomAny, i: number) => i !== index);
      remove(index);
      setDiscounts(formatDiscounts(updatedList));
    },
    [form],
  );

  const handleSubmit = useCallback(async (): Promise<void> => {
    formInstance.setFieldsValue({ discounts });
    handleClose();
  }, [discounts, formInstance, handleClose]);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center py-5">
        <div className="font-semibold text-3xl">{t('Discount settings')}</div>
        <div className="text-primaryLight font-normal cursor-pointer" onClick={handleClose}>
          {t('Close')}
        </div>
      </div>

      <Input label={t('Discount')} value={JSON.stringify(discounts)} readOnly bordered />

      <Divider className="mb-6 mt-0" />

      <Form form={form} layout="vertical" onValuesChange={handleFormChange} onFinish={handleSubmit}>
        <Form.List name="discountSettings">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name }) => (
                <div key={key} className="flex flex-col gap-2 mb-6 border border-border p-4 rounded-md">
                  <Form.Item name={[name, 'mode']} className="mb-2">
                    <RadioButtonGroup
                      options={getDiscountModeOptions(name)}
                      withBorder
                      onChange={(mode) => {
                        changeDiscountMode(mode, name);
                      }}
                      labelPosition="post"
                      optionType="button"
                      direction="vertical"
                      buttonClassName="w-fit rounded-lg"
                    />
                  </Form.Item>

                  <div className="flex gap-4 items-center">
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const mode = form.getFieldValue(['discountSettings', name, 'mode']);
                        return (
                          <Form.Item
                            name={[name, 'period']}
                            className="flex-1"
                            rules={[{ required: true, message: t('Enter period') }]}
                            label={t('Period')}
                          >
                            <InputNumber
                              placeholder={t('Period')}
                              bordered
                              isDisabled={mode === DiscountMode.ENTIRE_PERIOD || mode === DiscountMode.MIN_RENTAL_DURATION}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>

                    <div className="flex flex-1 gap-2">
                      <Form.Item name={[name, 'value']} rules={[{ required: true, message: t('Enter value') }]} label={t('Value')}>
                        <InputNumber placeholder={t('Value')} bordered />
                      </Form.Item>

                      <Form.Item name={[name, 'type']} label={t('Type')} className="w-[150px]">
                        <Select options={valueTypeOptions} placeholder={t('Type')} bordered />
                      </Form.Item>
                    </div>

                    <div
                      className="w-[44px] h-[44px] mt-1 rounded-full bg-secondary flex justify-center items-center cursor-pointer"
                      onClick={() => {
                        removeDiscountSetting(name, remove);
                      }}
                    >
                      <CancelIcon className="cursor-pointer [&>path]:stroke-primary" width={32} height={32} />
                    </div>
                  </div>
                </div>
              ))}

              <Button
                theme="outlined"
                icon={<PlusIcon className="[&>path]:stroke-primary" />}
                iconPosition="prev"
                onClick={() => {
                  add();
                }}
              >
                {t('Add')}
              </Button>
            </>
          )}
        </Form.List>
        <Button type="submit" className="mt-10">
          {t('Save')}
        </Button>
      </Form>
    </div>
  );
});
